import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Modal } from '@/components/synapse/Modal';
import { UserSearch } from '@/features/platform-admin/UserSearch';
import {
  useGrantSuperAdmin,
  useRevokeSuperAdmin,
  useSuperAdmins,
} from '@/hooks/usePlatformAdmin';
import { translateApiError } from '@/lib/apiError';
import type { PlatformUser } from '@/types/api';

/**
 * Who operates the platform.
 *
 * Promote-only: this screen never creates an account. Minting one would mean an
 * operator typing somebody else's initial password, which is precisely the
 * account takeover the audit trail exists to make visible. People register
 * themselves and are promoted here; the very first operator comes from
 * `bun run create-super-admin`, because at bootstrap there is nobody to promote
 * them.
 */
export function PlatformSuperAdminsPage() {
  const { t } = useTranslation();
  const [addOpen, setAddOpen] = useState(false);
  const [demoteTarget, setDemoteTarget] = useState<PlatformUser | null>(null);
  const { data: admins = [], isLoading, error } = useSuperAdmins();

  // At least one operator must remain, and the server refuses the last demotion
  // with a 409. Showing that as a disabled button with the reason attached beats
  // letting the request out and translating a conflict — the same choice the
  // roles page makes for its last access-managing role. The 409 is still handled
  // in the dialog, because another operator can demote concurrently.
  const isLastOne = admins.length <= 1;

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-[-0.02em]">
            {t('platformAdmin.superAdmins.title')}
          </h1>
          <p className="mt-1 text-[13.5px] text-muted">
            {t('platformAdmin.superAdmins.subtitle')}
          </p>
        </div>
        <Button onClick={() => setAddOpen(true)}>{t('platformAdmin.superAdmins.add')}</Button>
      </header>

      <Card className="overflow-hidden p-0">
        {isLoading ? (
          <div className="px-5 py-10 text-center text-[13px] text-muted">
            {t('platformAdmin.loading')}
          </div>
        ) : error ? (
          <div className="px-5 py-10 text-center text-[13px] text-destructive">
            {translateApiError(error, { fallback: t('platformAdmin.superAdmins.loadFailed') })}
          </div>
        ) : (
          <ul className="divide-y divide-line">
            {admins.map((admin) => (
              <li key={admin.id} className="flex items-center gap-3 px-4 py-3.5 sm:px-5">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[14px] font-medium tracking-[-0.01em]">
                    {admin.name}
                  </div>
                  <div className="truncate text-[12.5px] text-muted">{admin.email}</div>
                </div>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={isLastOne}
                  title={isLastOne ? t('platformAdmin.superAdmins.lastOneHint') : undefined}
                  aria-label={`${t('platformAdmin.superAdmins.demote')} ${admin.email}`}
                  onClick={() => setDemoteTarget(admin)}
                >
                  {t('platformAdmin.superAdmins.demote')}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {addOpen && <AddSuperAdminDialog onClose={() => setAddOpen(false)} />}
      {demoteTarget && (
        <DemoteDialog user={demoteTarget} onClose={() => setDemoteTarget(null)} />
      )}
    </div>
  );
}

function AddSuperAdminDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const grant = useGrantSuperAdmin();

  return (
    <Modal
      title={t('platformAdmin.superAdmins.addTitle')}
      description={t('platformAdmin.superAdmins.addDesc')}
      onClose={onClose}
      isPending={grant.isPending}
      footer={
        <Button variant="secondary" full onClick={onClose} disabled={grant.isPending}>
          {t('common.close')}
        </Button>
      }
    >
      <UserSearch
        query={query}
        onQueryChange={setQuery}
        label={t('platformAdmin.superAdmins.searchLabel')}
        placeholder={t('platformAdmin.superAdmins.searchPlaceholder')}
        action={(user) =>
          user.isSuperAdmin ? (
            <span className="text-[12px] text-muted">
              {t('platformAdmin.superAdmins.alreadyOne')}
            </span>
          ) : (
            <Button
              size="sm"
              disabled={grant.isPending}
              aria-label={`${t('platformAdmin.superAdmins.promote')} ${user.email}`}
              onClick={() => grant.mutate(user.id)}
            >
              {t('platformAdmin.superAdmins.promote')}
            </Button>
          )
        }
      />

      {grant.isError && (
        <div className="rounded-md bg-destructive-bg/40 px-3 py-2 text-[12.5px] text-destructive">
          {translateApiError(grant.error, { fallback: t('platformAdmin.superAdmins.promoteFailed') })}
        </div>
      )}
    </Modal>
  );
}

function DemoteDialog({ user, onClose }: { user: PlatformUser; onClose: () => void }) {
  const { t } = useTranslation();
  const revoke = useRevokeSuperAdmin();

  const submit = async () => {
    try {
      await revoke.mutateAsync(user.id);
      onClose();
    } catch {
      // Inline below — most likely a 409 from a concurrent demotion.
    }
  };

  return (
    <Modal
      title={t('platformAdmin.superAdmins.demoteTitle', { name: user.name })}
      description={t('platformAdmin.superAdmins.demoteDesc', { email: user.email })}
      onClose={onClose}
      isPending={revoke.isPending}
      footer={
        <>
          <Button variant="secondary" full onClick={onClose} disabled={revoke.isPending}>
            {t('common.cancel')}
          </Button>
          <Button variant="danger" full onClick={submit} disabled={revoke.isPending}>
            {revoke.isPending ? t('common.saving') : t('platformAdmin.superAdmins.demote')}
          </Button>
        </>
      }
    >
      {revoke.isError && (
        <div className="rounded-md bg-destructive-bg/40 px-3 py-2 text-[12.5px] text-destructive">
          {translateApiError(revoke.error, { fallback: t('platformAdmin.superAdmins.demoteFailed') })}
        </div>
      )}
    </Modal>
  );
}
